import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useEducationCategories, useEducationModules, useUserProgress } from '@/hooks/useEducation';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/navigation/Header';
import Footer from '@/components/sections/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, BookOpen, Clock, Award, Play, CheckCircle } from 'lucide-react';

const EducationCategory: React.FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const { user } = useAuth();
  const { data: categories } = useEducationCategories();
  const { data: modules } = useEducationModules(categoryId);
  const { data: userProgress } = useUserProgress(user?.id);

  const category = categories?.find(c => c.id === categoryId);

  const getModuleProgress = (moduleId: string) => {
    return userProgress?.find(p => p.module_id === moduleId);
  };

  const getOverallProgress = () => {
    if (!modules || modules.length === 0) return 0;
    const completedCount = modules.filter(module => {
      const progress = getModuleProgress(module.id);
      return progress?.status === 'completed';
    }).length;
    return (completedCount / modules.length) * 100;
  };

  const getTotalPoints = () => {
    return modules?.reduce((total, module) => {
      const progress = getModuleProgress(module.id);
      return total + (progress?.status === 'completed' ? module.points : 0);
    }, 0) || 0;
  };

  if (!category) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold">Category not found</h1>
            <Link to="/education" className="text-primary hover:underline">
              Back to Education
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <Link 
            to="/education" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Education
          </Link>
          
          <div className="space-y-4">
            <h1 className="text-4xl font-bold">{category.name}</h1>
            <p className="text-xl text-muted-foreground">{category.description}</p>
            
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                <span>{modules?.length || 0} modules</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{modules?.reduce((sum, m) => sum + m.duration_minutes, 0) || 0} minutes total</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="w-4 h-4" />
                <span>{getTotalPoints()} points earned</span>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Overall Progress</span>
                <span>{Math.round(getOverallProgress())}%</span>
              </div>
              <Progress value={getOverallProgress()} className="h-3" />
            </div>
          </div>
        </div>

        <div className="grid gap-6">
          {modules?.map((module, index) => {
            const progress = getModuleProgress(module.id);
            const isCompleted = progress?.status === 'completed';
            const isInProgress = progress?.status === 'in_progress';
            
            return (
              <Card key={module.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground font-bold text-sm">
                          {index + 1}
                        </div>
                        <CardTitle className="text-xl">{module.title}</CardTitle>
                        {isCompleted && (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        )}
                      </div>
                      <CardDescription>{module.description}</CardDescription>
                    </div>
                    <Badge variant={isCompleted ? 'default' : isInProgress ? 'secondary' : 'outline'}>
                      {isCompleted ? 'Completed' : isInProgress ? 'In Progress' : 'Not Started'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        <span>{module.duration_minutes} min</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Award className="w-4 h-4" />
                        <span>{module.points} points</span>
                      </div>
                    </div>
                    
                    <Button asChild>
                      <Link to={`/education/module/${module.id}`}>
                        <Play className="w-4 h-4 mr-2" />
                        {isCompleted ? 'Review' : isInProgress ? 'Continue' : 'Start'}
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {getOverallProgress() === 100 && (
          <Card className="mt-8 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-700">
                <Award className="w-5 h-5" />
                Congratulations!
              </CardTitle>
              <CardDescription className="text-green-600">
                You've completed all modules in this course. You can now download your certificate!
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="bg-green-600 hover:bg-green-700">
                Download Certificate
              </Button>
            </CardContent>
          </Card>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default EducationCategory;